/**
 * CSV parsing for price banner product upload with store column (shared by ListImport storesInCsv and AddProduct).
 */

export const PRICEBANNER_INVALID_STORES_I18N_KEY = 'price.banner.error.invalid.stores.values';
export const CMPM_PRODUCT_EXTRAS_STORAGE_KEY = 'cmpmProductExtras';
export const CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY = 'clubeLeroyProductExtras';
export const CLUBE_LEROY_THEME_CODE = 'LMVC_2PREC';

export function isCmpmThemeCode(themeCode) {
  return themeCode === 'CMPM' || themeCode === 'CMPMCRED';
}

export function isClubeLeroyThemeCode(themeCode) {
  return themeCode === CLUBE_LEROY_THEME_CODE;
}

export function getProductExtrasStorageKeyByTheme(themeCode) {
  if (isClubeLeroyThemeCode(themeCode)) return CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY;
  if (isCmpmThemeCode(themeCode)) return CMPM_PRODUCT_EXTRAS_STORAGE_KEY;
  return null;
}

export function normalizeCsvCell(cell) {
  return String(cell || '')
    .trim()
    .replace(/^\ufeff/, '')
    .replace(/^"|"$/g, '');
}

export function normalizeCsvHeader(cell) {
  return normalizeCsvCell(cell).toLowerCase();
}

export function isStoreColumnHeader(header) {
  return (
    header === 'lojas' ||
    header === 'loja' ||
    header === 'stores' ||
    header === 'store' ||
    header.includes('loja')
  );
}

export function detectCsvSeparator(line) {
  const counts = [
    [';', (line.match(/;/g) || []).length],
    [',', (line.match(/,/g) || []).length],
    ['\t', (line.match(/\t/g) || []).length],
  ];
  const best = counts.sort((a, b) => b[1] - a[1])[0];
  if (best[1] > 0) return best[0];
  return line.includes(';') ? ';' : ',';
}

export function splitCsvLine(line, separator) {
  const cols = line.split(separator).map(normalizeCsvCell);
  if (cols.length >= 2) return cols;
  for (const sep of [';', ',', '\t']) {
    if (sep !== separator) {
      const alt = line.split(sep).map(normalizeCsvCell);
      if (alt.length >= 2) return alt;
    }
  }
  return cols;
}

function fail(errorMessage) {
  return { ok: false, errorMessage };
}

/**
 * Conta linhas removidas como duplicadas.
 * Com lojas no CSV, só conta pares idênticos LM+loja (mesmo LM em lojas diferentes não é duplicata).
 */
export function countCsvProductDuplicatesRemoved(codes, branches, storesInCsv) {
  if (!codes || codes.length === 0) return 0;

  if (storesInCsv && branches && branches.length === codes.length) {
    const seen = new Set();
    let removed = 0;
    for (let i = 0; i < codes.length; i++) {
      const key = `${codes[i]}|${branches[i]}`;
      if (seen.has(key)) {
        removed += 1;
      } else {
        seen.add(key);
      }
    }
    return removed;
  }

  return codes.length - new Set(codes).size;
}

function detectCmpmSeparator(line, storesInCsv, fallbackSeparator) {
  const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;

  if (storesInCsv) {
    for (const sep of [';', '\t', ',']) {
      const cols = line.split(sep).map((c) => c.trim());
      if (cols.length === 4 || cols.length === 5) return sep;
    }
  }

  for (const sep of [';', '\t', ',']) {
    const cols = line.split(sep).map((c) => c.trim());
    if (cols.length === 3) return sep;
    if (
      cols.length === 4 &&
      /^\d{8}$/.test(cols[0]) &&
      quantityDecimalPointRegex.test(cols[1]) &&
      /^\d+$/.test(cols[2]) &&
      /^\d{1,2}$/.test(cols[3])
    ) {
      return sep;
    }
    if (cols.length === 1 && sep === ',') {
      const inner = line.split(';').map((c) => c.trim());
      if (inner.length === 3) return ';';
    }
  }
  return fallbackSeparator;
}

function parseCmpmLine(line, sep, storesInCsv) {
  const columns = line.split(sep).map((col) => col.trim());

  if (columns.length === 3) {
    return { lm: columns[0], quantity: columns[1], discountPercent: columns[2] };
  }

  if (columns.length === 4) {
    const lm = columns[0];
    const quantity = columns[1];
    const third = columns[2];
    const fourth = columns[3];

    if (storesInCsv) {
      if (/^\d{1,3}([.,]\d{1,2})?$/.test(third) && fourth) {
        return { lm, quantity, discountPercent: third, store: fourth };
      }
      const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;
      const isPromoDecimalSplit =
        /^\d{8}$/.test(lm) &&
        quantityDecimalPointRegex.test(quantity) &&
        /^\d+$/.test(third) &&
        /^\d{1,2}$/.test(fourth);

      if (isPromoDecimalSplit) {
        return { lm, quantity, discountPercent: `${third},${fourth}` };
      }
      return null;
    }

    const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;
    const isPromoDecimalSplit =
      /^\d{8}$/.test(lm) &&
      quantityDecimalPointRegex.test(quantity) &&
      /^\d+$/.test(third) &&
      /^\d{1,2}$/.test(fourth);

    if (isPromoDecimalSplit) {
      return { lm, quantity, discountPercent: `${third},${fourth}` };
    }
  }

  if (columns.length === 5 && storesInCsv) {
    const lm = columns[0];
    const quantity = columns[1];
    const promoIntPart = columns[2];
    const promoDecimalPart = columns[3];
    const storeColumn = columns[4];
    const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;
    const isPromoDecimalSplit =
      /^\d{8}$/.test(lm) &&
      quantityDecimalPointRegex.test(quantity) &&
      /^\d+$/.test(promoIntPart) &&
      /^\d{1,2}$/.test(promoDecimalPart) &&
      !!storeColumn;

    if (isPromoDecimalSplit) {
      return {
        lm,
        quantity,
        discountPercent: `${promoIntPart},${promoDecimalPart}`,
        store: storeColumn,
      };
    }
  }

  if (columns.length === 1 && (line.includes(';') || line.includes(','))) {
    const altSep = line.includes(';') ? ';' : ',';
    return parseCmpmLine(line, altSep, storesInCsv);
  }
  return null;
}

function validateCmpmNumericFields(lm, quantity, discountPercent) {
  const isQuantityEmpty = quantity === null || quantity === undefined || String(quantity).trim() === '';
  const isDiscountPercentEmpty =
    discountPercent === null || discountPercent === undefined || String(discountPercent).trim() === '';

  if (!/^\d{8}$/.test(lm)) {
    return { valid: false, errorMessage: 'price.banner.file.pattern.invalid' };
  }

  const quantityRegex = /^\d{1,7}([.,]\d{1,3})?$/;
  const normalizedQuantity = String(quantity).replace(',', '.');
  const parsedQuantity = parseFloat(normalizedQuantity);
  const quantityNegativeOrZero = Number.isFinite(parsedQuantity) && parsedQuantity <= 0;
  const quantityInvalidFormat =
    !quantityRegex.test(quantity) && !/^-[0-9]{1,7}(\.[0-9]{1,3})?$/.test(String(quantity).trim());

  if (!Number.isFinite(parsedQuantity) || quantityInvalidFormat) {
    return {
      valid: false,
      errorMessage:
        isQuantityEmpty || isDiscountPercentEmpty
          ? 'cmpm.empty.fields'
          : 'cmpm.invalid.values',
    };
  }
  if (quantityNegativeOrZero) {
    return {
      valid: false,
      errorMessage:
        isQuantityEmpty || isDiscountPercentEmpty
          ? 'cmpm.empty.fields'
          : 'cmpm.zero.negative',
    };
  }

  const valueRegex = /^\d{1,3}([.,]\d{1,2})?$/;
  const normalizedDiscountPercent = String(discountPercent).replace(',', '.');
  const parsedDiscountPercent = parseFloat(normalizedDiscountPercent);
  const discountNegativeOrZero = Number.isFinite(parsedDiscountPercent) && parsedDiscountPercent <= 0;
  const discountInvalidFormat =
    !valueRegex.test(discountPercent) && !/^-\d{1,3}([.,]\d{1,2})?$/.test(String(discountPercent).trim());

  if (discountInvalidFormat || parsedDiscountPercent > 99.99) {
    return {
      valid: false,
      errorMessage:
        isQuantityEmpty || isDiscountPercentEmpty
          ? 'cmpm.empty.fields'
          : 'cmpm.invalid.values',
    };
  }
  if (discountNegativeOrZero) {
    return {
      valid: false,
      errorMessage:
        isQuantityEmpty || isDiscountPercentEmpty
          ? 'cmpm.empty.fields'
          : 'cmpm.zero.negative',
    };
  }

  return {
    valid: true,
    normalizedQuantity,
    parsedDiscountPercent,
  };
}

function detectClubeLeroySeparator(line, storesInCsv, fallbackSeparator) {
  if (storesInCsv) {
    for (const sep of [';', '\t', ',']) {
      const cols = line.split(sep).map((c) => c.trim());
      if (cols.length === 3 || cols.length === 4) return sep;
    }
  }

  for (const sep of [';', '\t', ',']) {
    const cols = line.split(sep).map((c) => c.trim());
    if (cols.length === 2) return sep;
    if (
      cols.length === 3 &&
      /^\d{8}$/.test(cols[0]) &&
      /^\d+$/.test(cols[1]) &&
      /^\d+$/.test(cols[2])
    ) {
      return sep;
    }
    if (cols.length === 1 && sep === ',') {
      const inner = line.split(';').map((c) => c.trim());
      if (inner.length === 2) return ';';
    }
  }
  return fallbackSeparator;
}

function parseClubeLeroyLine(line, sep, storesInCsv) {
  const columns = line.split(sep).map((col) => col.trim());

  if (columns.length === 2) {
    if (storesInCsv) return null;
    return { lm: columns[0], discountPercent: columns[1] };
  }

  if (columns.length === 3) {
    const lm = columns[0];
    const second = columns[1];
    const third = columns[2];

    if (storesInCsv) {
      if (/^\d{1,3}([.,]\d+)?$/.test(second) && third) {
        return { lm, discountPercent: second, store: third };
      }
      return null;
    }

    const isDiscountDecimalSplit =
      /^\d{8}$/.test(lm) &&
      /^\d+$/.test(second) &&
      /^\d+$/.test(third);

    if (isDiscountDecimalSplit) {
      return { lm, discountPercent: `${second},${third}` };
    }
  }

  if (columns.length === 4 && storesInCsv) {
    const lm = columns[0];
    const discountIntPart = columns[1];
    const discountDecimalPart = columns[2];
    const storeColumn = columns[3];
    const isDiscountDecimalSplit =
      /^\d{8}$/.test(lm) &&
      /^\d+$/.test(discountIntPart) &&
      /^\d+$/.test(discountDecimalPart) &&
      !!storeColumn;

    if (isDiscountDecimalSplit) {
      return {
        lm,
        discountPercent: `${discountIntPart},${discountDecimalPart}`,
        store: storeColumn,
      };
    }
  }

  if (columns.length === 1 && (line.includes(';') || line.includes(','))) {
    const altSep = line.includes(';') ? ';' : ',';
    return parseClubeLeroyLine(line, altSep, storesInCsv);
  }
  return null;
}

function validateClubeLeroyNumericFields(lm, discountPercent) {
  const isDiscountPercentEmpty =
    discountPercent === null || discountPercent === undefined || String(discountPercent).trim() === '';

  if (!/^\d{8}$/.test(lm)) {
    return { valid: false, errorMessage: 'price.banner.file.pattern.invalid' };
  }

  const valueRegex = /^\d{1,3}([.,]\d{1,2})?$/;
  // Corta as casas excedentes no texto para não arredondar nem perder precisão no cálculo.
  const normalizedDiscountPercent = String(discountPercent)
    .replace(',', '.')
    .replace(/^(-?\d{1,3}\.\d{2})\d+$/, '$1');
  const parsedDiscountPercent = parseFloat(normalizedDiscountPercent);
  const discountNegativeOrZero = Number.isFinite(parsedDiscountPercent) && parsedDiscountPercent <= 0;
  const discountInvalidFormat =
    !valueRegex.test(normalizedDiscountPercent) && !/^-\d{1,3}([.,]\d{1,2})?$/.test(normalizedDiscountPercent.trim());

  if (discountInvalidFormat || parsedDiscountPercent > 99.99) {
    return {
      valid: false,
      errorMessage: isDiscountPercentEmpty ? 'clubeLeroy.empty.fields' : 'clubeLeroy.invalid.values',
    };
  }
  if (discountNegativeOrZero) {
    return {
      valid: false,
      errorMessage: isDiscountPercentEmpty ? 'clubeLeroy.empty.fields' : 'clubeLeroy.zero.negative',
    };
  }

  return {
    valid: true,
    parsedDiscountPercent,
  };
}

const CMPM_ERROR_MESSAGES = {
  'price.banner.file.pattern.invalid': 'price.banner.file.pattern.invalid',
  'cmpm.empty.fields':
    'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão vazios, realize o preenchimento no arquivo e repita a operação.',
  'cmpm.invalid.values':
    'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores acima do permitido, realize o ajuste no arquivo e repita a operação.',
  'cmpm.zero.negative':
    'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores zerados ou negativos, realize o ajuste no arquivo e repita a operação.',
};

function resolveCmpmError(key, translate) {
  if (key === 'price.banner.file.pattern.invalid') {
    return translate('price.banner.file.pattern.invalid');
  }
  return CMPM_ERROR_MESSAGES[key] || key;
}

const CLUBE_LEROY_ERROR_MESSAGES = {
  'price.banner.file.pattern.invalid': 'price.banner.file.pattern.invalid',
  'clubeLeroy.empty.fields':
    'Arquivo inválido, o campo "Percentual de desconto" está vazio, realize o preenchimento no arquivo e repita a operação.',
  'clubeLeroy.invalid.values':
    'Arquivo inválido, o campo "Percentual de desconto" está com valor acima do permitido, realize o ajuste no arquivo e repita a operação.',
  'clubeLeroy.zero.negative':
    'Arquivo inválido, o campo "Percentual de desconto" está com valor zerado ou negativo, realize o ajuste no arquivo e repita a operação.',
};

function resolveClubeLeroyError(key, translate) {
  if (key === 'price.banner.file.pattern.invalid') {
    return translate('price.banner.file.pattern.invalid');
  }
  return CLUBE_LEROY_ERROR_MESSAGES[key] || key;
}

function parseStandardWithStores(lines, separator, translate) {
  let lojasIndex = -1;
  const firstLineCols = splitCsvLine(lines[0], separator);
  const headerLower = firstLineCols.map(normalizeCsvHeader);
  const hasHeader = headerLower.some(isStoreColumnHeader);
  const codes = [];
  const collectedBranchesFromFile = [];
  const productBranchesMap = {};

  if (hasHeader) {
    lojasIndex = headerLower.findIndex(isStoreColumnHeader);
    let lmIndex = headerLower.findIndex((h) => h === 'material' || h === 'lm');
    if (lmIndex < 0) lmIndex = 0;

    for (const line of lines.slice(1)) {
      const cols = splitCsvLine(line, separator);
      const lm = (cols[lmIndex] || '').trim();
      const lojaVal = (cols[lojasIndex] || '').trim();
      if (!lm || !/^\d{8}$/.test(lm)) {
        return fail(translate('price.banner.file.pattern.invalid'));
      }
      if (!lojaVal) {
        return fail(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
      }
      codes.push(lm);
      collectedBranchesFromFile.push(lojaVal);
      productBranchesMap[lm] = [...(productBranchesMap[lm] || []), lojaVal];
    }
  } else {
    let dataLines = lines;
    const firstColsLower = splitCsvLine(lines[0], separator).map(normalizeCsvHeader);
    const firstLineIsHeader =
      firstColsLower.some(isStoreColumnHeader) ||
      firstColsLower.some((h) => h === 'lm' || h === 'material');
    if (firstLineIsHeader) {
      dataLines = lines.slice(1);
    }
    for (const line of dataLines) {
      const cols = splitCsvLine(line, separator);
      const lm = (cols[0] || '').trim();
      const lojaVal = (cols[cols.length - 1] || '').trim();
      if (!lm || !/^\d{8}$/.test(lm)) {
        return fail(translate('price.banner.file.pattern.invalid'));
      }
      if (!lojaVal) {
        return fail(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
      }
      codes.push(lm);
      collectedBranchesFromFile.push(lojaVal);
      productBranchesMap[lm] = [...(productBranchesMap[lm] || []), lojaVal];
    }
  }

  if (!codes.every((code) => /^\d{8}$/.test(code))) {
    return fail(translate('price.banner.file.pattern.invalid'));
  }

  return {
    ok: true,
    codes,
    productBranchesMap,
    productBranchExtrasMap: {},
    collectedBranchesFromFile,
    productExtrasMap: {},
  };
}

/**
 * Parses a product CSV that includes store column(s). Used by Add Product modal.
 */
export function parsePriceBannerProductCsvWithStores(content, listThemeCode, translate) {
  const storesInCsv = true;
  const lines = content.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) {
    return fail(translate('price.banner.file.empty'));
  }

  const separator = detectCsvSeparator(lines[0]);
  const isCmpm = isCmpmThemeCode(listThemeCode);
  const isClubeLeroy = isClubeLeroyThemeCode(listThemeCode);

  let codes = [];
  const collectedBranchesFromFile = [];
  const productExtrasMap = {};
  const productBranchesMap = {};
  const productBranchExtrasMap = {};

  if (isCmpm) {
    const hasHeader =
      lines[0].toLowerCase().includes('material') || lines[0].toLowerCase().includes('quantidade');
    const dataLines = hasHeader ? lines.slice(1) : lines;
    let usedSeparator = separator;
    if (dataLines.length > 0) {
      usedSeparator = detectCmpmSeparator(dataLines[0], storesInCsv, separator);
    }

    for (const line of dataLines) {
      if (/[eE]/.test(line)) {
        return fail(
          'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores acima do permitido, realize o ajuste no arquivo e repita a operação.'
        );
      }

      const parsed = parseCmpmLine(line, usedSeparator, storesInCsv);
      if (!parsed) {
        return fail(
          'Arquivo CMPM deve ter colunas: Material, Quantidade de unidades, Percentual de desconto e Lojas'
        );
      }

      const { lm, quantity, discountPercent, store } = parsed;
      const validation = validateCmpmNumericFields(lm, quantity, discountPercent);
      if (!validation.valid) {
        return fail(resolveCmpmError(validation.errorMessage, translate));
      }

      const storeValue = store || splitCsvLine(line, usedSeparator).slice(-1)[0] || '';
      if (!storeValue || String(storeValue).trim() === '') {
        return fail(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
      }
      const normalizedStore = String(storeValue).trim();

      codes.push(lm);
      collectedBranchesFromFile.push(normalizedStore);
      productBranchesMap[lm] = [...(productBranchesMap[lm] || []), normalizedStore];
      productBranchExtrasMap[lm] = [
        ...(productBranchExtrasMap[lm] || []),
        {
          idBranch: normalizedStore,
          quantity: validation.normalizedQuantity,
          discountPercent: validation.parsedDiscountPercent,
        },
      ];
      productExtrasMap[lm] = {
        quantity: validation.normalizedQuantity,
        discountPercent: validation.parsedDiscountPercent,
      };
    }
  } else if (isClubeLeroy) {
    const hasHeader =
      lines[0].toLowerCase().includes('material') || lines[0].toLowerCase().includes('desconto');
    const dataLines = hasHeader ? lines.slice(1) : lines;
    let usedSeparator = separator;
    if (dataLines.length > 0) {
      usedSeparator = detectClubeLeroySeparator(dataLines[0], storesInCsv, separator);
    }

    for (const line of dataLines) {
      if (/[eE]/.test(line)) {
        return fail(
          'Arquivo inválido, o campo "Percentual de desconto" está com valor acima do permitido, realize o ajuste no arquivo e repita a operação.'
        );
      }

      const parsed = parseClubeLeroyLine(line, usedSeparator, storesInCsv);
      if (!parsed) {
        return fail(
          'Arquivo Clube Leroy deve ter colunas: Material, Percentual de desconto e Lojas'
        );
      }

      const { lm, discountPercent, store } = parsed;
      const validation = validateClubeLeroyNumericFields(lm, discountPercent);
      if (!validation.valid) {
        return fail(resolveClubeLeroyError(validation.errorMessage, translate));
      }

      const storeValue = store || splitCsvLine(line, usedSeparator).slice(-1)[0] || '';
      if (!storeValue || String(storeValue).trim() === '') {
        return fail(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
      }
      const normalizedStore = String(storeValue).trim();

      codes.push(lm);
      collectedBranchesFromFile.push(normalizedStore);
      productBranchesMap[lm] = [...(productBranchesMap[lm] || []), normalizedStore];
      productBranchExtrasMap[lm] = [
        ...(productBranchExtrasMap[lm] || []),
        {
          idBranch: normalizedStore,
          discountPercent: validation.parsedDiscountPercent,
        },
      ];
      productExtrasMap[lm] = {
        discountPercent: validation.parsedDiscountPercent,
      };
    }
  } else {
    const standardResult = parseStandardWithStores(lines, separator, translate);
    if (!standardResult.ok) return standardResult;
    codes = standardResult.codes;
    Object.assign(productBranchesMap, standardResult.productBranchesMap);
    collectedBranchesFromFile.push(...standardResult.collectedBranchesFromFile);
  }

  const uniqueCodes = [...new Set(codes)];
  const duplicatesRemoved = countCsvProductDuplicatesRemoved(
    codes,
    collectedBranchesFromFile,
    storesInCsv
  );

  if (storesInCsv && collectedBranchesFromFile.length === 0) {
    return fail(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
  }

  return {
    ok: true,
    codes: uniqueCodes,
    originalCodesLength: codes.length,
    duplicatesRemoved,
    productBranchesByLm: productBranchesMap,
    productBranchExtrasByLm: productBranchExtrasMap,
    branchCodesFromFile: collectedBranchesFromFile,
    branchesFromFile: [...new Set(collectedBranchesFromFile)],
    productExtrasMap,
  };
}

export function getAddProductCsvTemplate(listThemeCode) {
  const isCmpm = isCmpmThemeCode(listThemeCode);
  const isClubeLeroy = isClubeLeroyThemeCode(listThemeCode);

  if (isCmpm) {
    return {
      content:
        'Material;Quantidade de unidades;Percentual de desconto;Lojas\n' +
        '89615435;10,625;99,90;0001\n' +
        '89337283;5,620;10,50;0002\n' +
        '12345678;20;0,00;0003',
      filename: 'model_add_products_cmpm_with_stores.csv',
    };
  }

  if (isClubeLeroy) {
    return {
      content:
        'Material;Percentual de desconto;Lojas\n' +
        '89615435;15,00;0001\n' +
        '89337283;10,50;0002\n' +
        '12345678;20;0003',
      filename: 'model_add_products_clube_leroy_with_stores.csv',
    };
  }

  return {
    content:
      'LM;Lojas\n' +
      '89615435;0001\n' +
      '89337283;0002\n' +
      '12345678;0003\n',
    filename: 'model_add_products_with_stores.csv',
  };
}

function filterMapByLms(sourceMap, validLms) {
  if (!sourceMap || typeof sourceMap !== 'object') return {};
  const filtered = {};
  validLms.forEach((lm) => {
    if (sourceMap[lm] != null) {
      filtered[lm] = sourceMap[lm];
    }
  });
  return filtered;
}

/** Keeps only branch data for valid LMs when building add-products metadata. */
export function filterBranchMapsForProductLms(maps, productLms) {
  const validSet = new Set(productLms);
  const filterKeys = (map) => {
    if (!map || typeof map !== 'object') return {};
    return Object.keys(map).reduce((acc, lm) => {
      if (validSet.has(lm)) acc[lm] = map[lm];
      return acc;
    }, {});
  };
  return {
    productBranchesByLm: filterKeys(maps.productBranchesByLm),
    productBranchExtrasByLm: filterKeys(maps.productBranchExtrasByLm),
    productExtrasByLm: filterMapByLms(maps.productExtrasByLm, productLms),
  };
}

